import moment from "moment";
import React, { useState, useEffect, useRef } from "react";
import { connect, useDispatch } from "react-redux";
import { Dispatch } from "interfaces/dispatch";
import { State } from "state";
import { FileUploadState, uploadFile } from "state/create_file_upload";
import { fetchProject } from "state/project";
import { AuthUser } from "state/auth_user";
import { Folder } from "lucide-react";
import { checkUsersChains } from "state/store";
import { checkUsers } from "state/users";
import UsersNotFoundModal from "./UsersNotFoundModal";
import UsersNotFoundInDatabaseModal from "./UsersNotFoundInDatabaseModal";
import AddUsersNotFoundModal from "./AddUsersNotFountModal";
import ExternalAdminModal from "../request-dashboard/ExternalAdminChainRequest";
import Config from "config";
import FileEditorModal from "components/FileEditorModal";
import { SeverityLevel, ToastService } from "services/toastService";
import { getItem } from "../../helpers/storage";
import { IMPERSONATIONUSER } from "../../state/auth";
import ErrorModal from "./ErrorModal";
// import ErrorModal from "./ErrorModal";

// Interface for the 'stores' array elements within 'notAssociatedUsers'
export interface UserStore {
  retail_store_num: any;
}

// Interface for the elements in the 'notAssociatedUsers' array
export interface NotAssociatedUser {
  id: number;
  email: string;
  stores: UserStore[];
}

// Main interface for the entire JSON output
export interface ChainAssociationResult {
  chainName: string;
  availableUserIds: number[];
  notAssociatedUsers: NotAssociatedUser[];
}

interface CheckUserEmails {
  availableEmails: any[];
  notAvailableEmails: any[];
}

interface FileUploadOwnProps {
  label: string;
  url: string;
  prefix: string;
  projectId?: string | number;
  fileStatus?: string;
  basicImport?: boolean;
  multiple?: boolean;
  disabled?: boolean;
  accept?: string;
  dataQa?: string;
  user?: AuthUser;
  project: any;
  isExternalAdmin: boolean;
  onChainCheckingChange?: (loading: boolean) => void;
}

interface FileUploadDispatchProps {
  upload: FileUploadState;
  afterUploadFunction?: (arg?: any) => void | Promise<any>;
  beforeUploadFunction?: (...args: any[]) => Promise<any>;
}

interface FileUploadReduxProps {
  fetchProject: (id: string | number) => void;
  uploadFile: (
    prefix: string,
    uploadUrl: string,
    options?: { offset?: number }
  ) => (data: FileUploadState) => Promise<void>;
}

type FileUploadProps = FileUploadOwnProps & FileUploadDispatchProps & FileUploadReduxProps;

const FileUploadForStep4CP: React.FC<FileUploadProps> = ({
  projectId,
  fileStatus,
  multiple,
  user,
  upload,
  afterUploadFunction,
  fetchProject: fetchProjectDispatch,
  uploadFile: uploadFileDispatch,
  project,
  isExternalAdmin,
  // pageName,
}) => {
  const [uploadedFiles, setUploadedFiles] = useState<string[]>([]);
  const [active, setActive] = useState(false);
  const [success, setSuccess] = useState(false);
  const timerRef = useRef<number>(-1);

  const [isStoreUploading, setIsStoreUploading] = useState(false);
  const [storeUploadError, setStoreUploadError] = useState<string>("");
  const [storeUploadSuccess, setStoreUploadSuccess] = useState(false);

  const storeFileRef = useRef<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const generatedFileRef = useRef<File | null>(null);

  const dispatch = useDispatch();

  const [retailStoreNumbers, setRetailStoreNumbers] = useState<string[]>([]);
  const [userEmailAddresses, setUserEmailAddresses] = useState<string[]>([]);

  const [notAvailableEmailsInDB, setNotAvailableEmailsInDB] = useState<string[]>([]);
  const [unMappedUsers, setUnMappedUsers] = useState<any[]>([]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isStoreModalOpen, setIsStoreModalOpen] = useState(false);

  const [isAddUserNotFountModalOpen, setIsAddUserNotFountModalOpen] = useState(false);

  const [fileEditorOpen, setFileEditorOpen] = useState<boolean>(false);
  const [fileEditorFile, setFileEditorFile] = useState<File | null>(null);
  const [fileEditorError, setFileEditorError] = useState<string | null>(null);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);

  // NEW: Add state to track user intent
  const [shouldProceedAfterDBCheck, setShouldProceedAfterDBCheck] = useState(false);
  
  // External admin modal state
  const [isExternalAdminModalOpen, setIsExternalAdminModalOpen] = useState(false);
  const [dbAvailableEmails, setDbAvailableEmails] = useState<string[]>([]);
  const [dbMissingEmails, setDbMissingEmails] = useState<string[]>([]);
  const [isChainChecking, setIsChainChecking] = useState(false);
  const [duplicateEmailsFound, setDuplicateEmailsFound] = useState<string[]>([]);

  const normalizeEmail = (email: string) => String(email || "").trim().toLowerCase();
  const getUniqueEmails = (emails: string[]) => {
    const seen = new Set<string>();
    return emails.reduce<string[]>((acc, email) => {
      const normalized = normalizeEmail(email);
      if (!normalized || seen.has(normalized)) {
        return acc;
      }
      seen.add(normalized);
      acc.push(String(email || "").trim());
      return acc;
    }, []);
  };

  useEffect(() => {
    return () => {
      clearTimeout(timerRef.current);
    };
  }, []);

  useEffect(() => {
    if (userEmailAddresses.length > 0) {
      handleUserCheckInDB();
    } else {
    }
  }, [userEmailAddresses]);

  const proceedWithMapping = async (emails: string[]) => {
    if (!storeFileRef.current) return;

    // Remove DB-missing users from CSV
    const filteredFile = await filterCsvDataByAvailableUsers(
      storeFileRef.current,
      emails
    );

    // Replace original file
    storeFileRef.current = filteredFile;

    // NOW call chain checks
    handleUsersChainCheck(emails);
  };

  const handleUserCheckInDB = async () => {
    const response = (await dispatch(
      checkUsers(getUniqueEmails(userEmailAddresses))
    )) as unknown as CheckUserEmails;

    const available = response.availableEmails || [];
    const missing = response.notAvailableEmails || [];

    setDbAvailableEmails(available);
    setDbMissingEmails(missing);
    setNotAvailableEmailsInDB(missing);

    if (missing.length > 0) {
      setIsModalOpen(true); // request modal
    } else {
      proceedWithMapping(available); // no request needed
    }
  };

  const addChainNameColumnToCsv = async (file: File): Promise<File> => {
    if (!project?.chain?.name || !file.name.toLowerCase().endsWith(".csv")) {
      return file;
    }

    const chainName = project.chain.name;
    const text = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const raw = reader.result as ArrayBuffer;
        const bytes = new Uint8Array(raw);
        let encoding = "utf-8";
        if (bytes[0] === 0xff && bytes[1] === 0xfe) encoding = "utf-16le";
        else if (bytes[0] === 0xfe && bytes[1] === 0xff) encoding = "utf-16be";
        else if (!(bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf)) {
          let valid = true;
          for (let i = 0; i < Math.min(bytes.length, 4096); i++) {
            const b = bytes[i];
            if (b > 0x7f && b < 0xa0) { valid = false; break; }
          }
          if (!valid) encoding = "windows-1252";
        }
        resolve(new TextDecoder(encoding).decode(raw));
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsArrayBuffer(file);
    });

    const lines = text.split(/\r?\n/).filter((line) => line.trim() !== "");
    if (lines.length === 0) {
      return file;
    }

    const headers = lines[0].split(",").map((header) => header.trim().replace(/^"|"$/g, ""));
    if (headers.some((header) => header.toLowerCase() === "chain name")) {
      return file;
    }

    const escapedChainName = chainName.replace(/"/g, '""');
    const quotedChainName = `"${escapedChainName}"`;

    const transformedLines = lines.map((line, index) => {
      if (index === 0) {
        return `Chain Name,${line}`;
      }
      return `${quotedChainName},${line}`;
    });

    return new File([transformedLines.join("\n")], file.name, {
      type: file.type || "text/csv",
      lastModified: Date.now(),
    });
  };

  // UPDATED: Only proceed if user confirmed via Submit button
  useEffect(() => {
    if (
      isModalOpen === false &&
      notAvailableEmailsInDB.length > 0 &&
      isStoreModalOpen === false &&
      shouldProceedAfterDBCheck
    ) {
      // ONLY users that exist in DB
      handleUsersChainCheck(dbAvailableEmails);
      setTimeout(() => {
        setNotAvailableEmailsInDB([]);
        setShouldProceedAfterDBCheck(false);
      }, 1000);
    }
  }, [isModalOpen, shouldProceedAfterDBCheck, dbAvailableEmails]);

  const handleUsersChainCheck = async (emails: string[]) => {
    const uniqueEmails = getUniqueEmails(emails);
    if (!uniqueEmails || uniqueEmails.length === 0) return;

    // TURN LOADER ON (ADD HERE)
    setIsChainChecking(true);

    let data = {
      chainName: project.chain.name,
      userEmails: uniqueEmails,
    };

    try {
      const response = (await dispatch(
        checkUsersChains(data)
      )) as unknown as ChainAssociationResult;

      if (response) {
        setUnMappedUsers(response.notAssociatedUsers);

        if (response.notAssociatedUsers.length > 0) {
          // Open external admin modal if user is external admin, otherwise open UsersNotFoundModal
          if (isExternalAdmin) {
            setIsExternalAdminModalOpen(true);
          } else {
            setIsStoreModalOpen(true);
          }
        } else {
          const filesToUpload: File[] = storeFileRef.current ? [storeFileRef.current] : [];
          onDrop("USER2STORE", `${Config.API_SERVICE_URL}/api/users-stores`, filesToUpload);
        }
      }
    } catch (error) {
      setStoreUploadError("Failed to check stores");
    } finally {
      // ✅ TURN LOADER OFF (ADD HERE)
      setIsChainChecking(false);
    }
  };
  const prevLoadingRef = useRef<boolean | undefined>(false);

  useEffect(() => {
    const { loading, hasError, content, errors } = upload;
    const prevLoading = prevLoadingRef.current;

    // Upload finished successfully
    if (prevLoading && !loading && !hasError && content) {
      setSuccess(true);
      setActive(false);
    }

    // Upload finished with error → OPEN MODAL
    if (prevLoading && !loading && hasError) {
      setErrorMessages(errors || ["Something went wrong"]);
      setIsErrorModalOpen(true);
    }

    prevLoadingRef.current = loading;
  }, [upload.loading, upload.hasError, upload.content, upload.errors]);

  const filterCsvDataByAvailableUsers = (
    file: File,
    allowedEmails: string[]
  ): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = async () => {
        try {
          const raw = reader.result as ArrayBuffer;
          const bytes = new Uint8Array(raw);
          let encoding = "utf-8";
          if (bytes[0] === 0xff && bytes[1] === 0xfe) encoding = "utf-16le";
          else if (bytes[0] === 0xfe && bytes[1] === 0xff) encoding = "utf-16be";
          else if (!(bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf)) {
            let valid = true;
            for (let i = 0; i < Math.min(bytes.length, 4096); i++) {
              if (bytes[i] > 0x7f && bytes[i] < 0xa0) { valid = false; break; }
            }
            if (!valid) encoding = "windows-1252";
          }
          const text = new TextDecoder(encoding).decode(raw);
          const lines = text.split("\n").filter(Boolean);

          const headers = lines[0];
          const normalizedAllowed = new Set(
            allowedEmails.map((email) => normalizeEmail(email)).filter((email) => email !== "")
          );
          const seenEmails = new Set<string>();
          const filteredRows = lines.slice(1).filter(row => {
            const cols = row.split(",");
            const email = cols[headers
              .split(",")
              .findIndex(h => h.trim() === "Email Address")]?.trim();
            const normalized = normalizeEmail(email || "");
            if (!normalized || !normalizedAllowed.has(normalized) || seenEmails.has(normalized)) {
              return false;
            }

            seenEmails.add(normalized);
            return true;
          });

          const newCsv = [headers, ...filteredRows].join("\n");

          const newFile = new File([newCsv], file.name, {
            type: "text/csv",
            lastModified: Date.now(),
          });

          const fileWithChainName = await addChainNameColumnToCsv(newFile);
          resolve(fileWithChainName);
        } catch (e) {
          reject(e);
        }
      };

      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };

  const extractMultipleColumnData = async (
    file: File
  ): Promise<{ fileData: string[]; storeNumberData: string[]; rawEmailData: string[] }> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e: any) => {
        try {
          const data = e.target.result;
          const fileData: string[] = [];
          const storeNumberData: string[] = [];
          const rawEmailData: string[] = [];

          if (file.name.endsWith(".csv")) {
            const raw = data as ArrayBuffer;
            const bytes = new Uint8Array(raw);
            let encoding = "utf-8";
            if (bytes[0] === 0xff && bytes[1] === 0xfe) encoding = "utf-16le";
            else if (bytes[0] === 0xfe && bytes[1] === 0xff) encoding = "utf-16be";
            else if (!(bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf)) {
              let valid = true;
              for (let i = 0; i < Math.min(bytes.length, 4096); i++) {
                if (bytes[i] > 0x7f && bytes[i] < 0xa0) { valid = false; break; }
              }
              if (!valid) encoding = "windows-1252";
            }
            const text = new TextDecoder(encoding).decode(raw);
            const lines = text.split("\n").filter((line) => line.trim() !== "");

            if (lines.length > 0) {
              const headers = lines[0].split(",").map((col: string) => col.trim());
              const fileColumnIndex = headers.findIndex(
                (header) => header === "Retail Store Number"
              );
              const storeNumberColumnIndex = headers.findIndex(
                (header) => header === "Email Address"
              );
              const seenEmails = new Set<string>();

              for (let i = 1; i < lines.length; i++) {
                const row = lines[i].split(",");

                if (fileColumnIndex !== -1 && row[fileColumnIndex]) {
                  const fileValue = row[fileColumnIndex].trim();
                  if (fileValue) fileData.push(fileValue);
                }

                if (storeNumberColumnIndex !== -1 && row[storeNumberColumnIndex]) {
                  const storeNumberValue = row[storeNumberColumnIndex].trim();
                  const normalized = normalizeEmail(storeNumberValue);
                  if (storeNumberValue) {
                    rawEmailData.push(storeNumberValue);
                    if (!seenEmails.has(normalized)) {
                      seenEmails.add(normalized);
                      storeNumberData.push(storeNumberValue);
                    }
                  }
                }
              }
            }
          } else {
            reject(new Error("Unsupported file format"));
          }

          resolve({ fileData, storeNumberData, rawEmailData });
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = () => reject(new Error("Failed to read file"));

      if (file.name.endsWith(".xlsx") || file.name.endsWith(".xls")) {
        reader.readAsBinaryString(file);
      } else {
        reader.readAsArrayBuffer(file);
      }
    });
  };

  const handleStoreFileSelect = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const file = files[0];

    storeFileRef.current = file;

    try {
      const { fileData, storeNumberData, rawEmailData } = await extractMultipleColumnData(file);
      const seenOnce = new Set<string>();
      const seenTwice = new Set<string>();
      rawEmailData.forEach((e) => {
        const n = normalizeEmail(e);
        if (seenOnce.has(n)) seenTwice.add(n);
        else seenOnce.add(n);
      });
      const duplicates = storeNumberData.filter((e) => seenTwice.has(normalizeEmail(e)));

      setRetailStoreNumbers(fileData);
      setDuplicateEmailsFound(duplicates);
      setUserEmailAddresses(storeNumberData);
    } catch (error) {
      console.error("Error extracting file data:", error);
      setStoreUploadError("Failed to read file");
    }
  };

  const handleUpload = (
    prefixArg: string,
    urlArg: string,
    data: FormData,
    options?: { offset?: number; timezone?: string },
    fileStatusArg?: string,
    // projectIdArg?: string | number,
    projectId?: string | number,
    userIdArg?: number
    // projectId: string
  ) => {
    if (projectId) {
      data.append("projectId", `${projectId}`);
    }

    // Append chainName from frontend when available
    if (project && project.chain && project.chain.name) {
      data.append("chainName", `${project.chain.name}`);
    }

    if (fileStatusArg) {
      data.append("fileStatus", `${fileStatusArg}`);
    }

    if (userIdArg) {
      data.append("userId", `${userIdArg}`);
    }

    // IMPORTANT: Return the entire promise chain
    return uploadFileDispatch(
      prefixArg,
      urlArg,
      options
    )({ data })
      .then(() => {
        if (afterUploadFunction) {
          afterUploadFunction();
        }
      })
      .then(() => {
        if (projectId) {
          fetchProjectDispatch(projectId);
        }
      })
      .catch((error) => {
        console.error("uploadFileDispatch failed in onDrop:", error);
        ToastService.getInstance().publishToast(
          {
            identifier: "error-operation",
            title: "Error",
            message: error,
          },
          SeverityLevel.ERROR,
          { autoClose: 3000 }
        );
      });
  };

  const onDrop = async (prefix: any, url: any, files: File[]) => {
    const storedUser = getItem(IMPERSONATIONUSER);
    let value
    if (storedUser) {
      value = JSON.parse(storedUser)
    }
    const overRideUser = value || user
    const userId = overRideUser?.id;
    const data = new FormData();

    if (files) {
      for (let file of files) {
        if (file) {
          setUploadedFiles((prev) => [file.name, ...prev]);
          if (prefix === "USER2STORE") {
            file = await addChainNameColumnToCsv(file);
          }
          if (multiple) {
            data.append("files", file);
          } else {
            data.append("file", file);
          }
        }
      }
    }

    const offset = moment().utcOffset();
    const timezone = moment.tz.guess();
    const options = { offset, timezone };

    // handleUpload(prefix, url, data, options, fileStatus, projectId, userId);

    try {
      await handleUpload(prefix, url, data, options, fileStatus, projectId, userId);

      setIsAddUserNotFountModalOpen(false);

      const filesToUpload2: File[] = storeFileRef.current ? [storeFileRef.current] : [];
      onDrop2("USER2STORE", `${Config.API_SERVICE_URL}/api/users-stores`, filesToUpload2);
      // handleUserStores();
    } catch (error) {
      console.error("Upload failed in onDrop:", error);
      ToastService.getInstance().publishToast(
        {
          identifier: "error-operation",
          title: "Error",
          message: error,
        },
        SeverityLevel.ERROR,
        { autoClose: 3000 }
      );
      // You may want to handle errors here (e.g., show an error message)
    }
  };

  const onDrop2 = async (prefix: any, url: any, files: File[]) => {
    const userId = user?.id;
    const data = new FormData();

    if (files) {
      for (let file of files) {
        if (file) {
          setUploadedFiles((prev) => [file.name, ...prev]);
          if (prefix === "USER2STORE") {
            file = await addChainNameColumnToCsv(file);
          }
          if (multiple) {
            data.append("files", file);
          } else {
            data.append("file", file);
          }
        }
      }
    }

    const offset = moment().utcOffset();
    const timezone = moment.tz.guess();
    const options = { offset, timezone };

    try {
      await handleUpload(prefix, url, data, options, fileStatus, projectId, userId);
    } catch (error) {
      console.error("Upload failed in onDrop:", error);
      ToastService.getInstance().publishToast(
        {
          identifier: "error-operation",
          title: "Error",
          message: error,
        },
        SeverityLevel.ERROR,
        { autoClose: 3000 }
      );
    }
  };

  const generateUserRolesCSV = (
    chainName: string,
    usersWithRoles: { email: string; role: string }[]
  ): File => {
    const headers = ["Chain Name", "Email Address", "Role"];

    const csvRows = usersWithRoles.map((user) =>
      [
        `"${chainName}"`, // Wrap in quotes to handle spaces/commas safely
        `"${user.email}"`,
        `"${user.role}"`,
        `0`,
        `0`,
      ].join(",")
    );

    const csvContent = [headers.join(","), ...csvRows].join("\n");

    const dataBlob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const fileName = `${chainName}_User_Roles_${moment().format("YYYYMMDD_HHmmss")}.csv`;
    const csvFile = new File([dataBlob], fileName, {
      type: "text/csv",
      lastModified: Date.now(),
    });

    return csvFile;
  };

  const handleEditedFileCall = async (editedFile: any) => {
    storeFileRef.current = editedFile;

    const { fileData, storeNumberData } = await extractMultipleColumnData(editedFile);
    setRetailStoreNumbers(fileData);
    setUserEmailAddresses(storeNumberData);
  };

  const handleYes = () => {
    setIsStoreModalOpen(false);
    setIsAddUserNotFountModalOpen(true);
  };

  const handleNo = () => {
    setIsStoreModalOpen(false);
    // setUploadFlow("idle");
    handleUploadError(unMappedUsers.join(", "), storeFileRef.current!);
  };

  const handleUploadError = (error: any, uploadedFile: File) => {

    const errorMessage =
      error?.response?.data?.message ||
      error?.message ||
      "Upload failed. Please review and correct the file.";

    setFileEditorOpen(true);
    setFileEditorFile(uploadedFile);
    setFileEditorError(errorMessage);
  };

  const handleFileEditorClose = () => {
    setFileEditorOpen(false);
    setFileEditorFile(null);
    setFileEditorError(null);
  };

  const handleFileEditorSuccess = async () => {
    handleFileEditorClose();
  };

  return (
    <div style={styles.uploadSection}>
      <div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={(e) => {handleStoreFileSelect(e.target.files);
            e.target.value = "";
          }}
          style={{ display: "none" }}
        />

        <div
          onClick={() => fileInputRef.current?.click()}
          onDrop={(e) => {
            e.preventDefault();
            handleStoreFileSelect(e.dataTransfer.files);
          }}
          onDragOver={(e) => e.preventDefault()}
          style={{
            border: "2px dashed #ccc",
            borderRadius: "8px",
            padding: "0 10px",
            textAlign: "center",
            cursor: "pointer",
            marginBottom: "20px",
            backgroundColor: "#fafafa",
            transition: "all 0.3s",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 10,
          }}
        >
          <Folder size={32} style={{ color: "#ccc" }} />
          <p>
            Drag and drop Mapping files here, or
            <span style={{ color: "#E5B42F", marginRight: 5 }}> click to upload</span>
          </p>
        </div>

        {isStoreUploading && (
          <div style={{ padding: "10px", color: "#1976d2", marginTop: "10px" }}>Uploading...</div>
        )}

        {storeUploadError && (
          <div
            style={{
              padding: "10px",
              backgroundColor: "#ffebee",
              borderRadius: "4px",
              color: "#c62828",
              marginTop: "10px",
            }}
          >
            {storeUploadError}
          </div>
        )}

        {storeUploadSuccess && (
          <div
            style={{
              padding: "10px",
              backgroundColor: "#e8f5e9",
              borderRadius: "4px",
              color: "#2e7d32",
              marginTop: "10px",
            }}
          >
            ✓ Store file uploaded successfully
          </div>
        )}
      </div>

      <FileEditorModal
        callCallbackFunction={true}
        callOuterFunction={(editedFile) => {
          handleEditedFileCall(editedFile);
        }}
        isOpen={fileEditorOpen}
        file={fileEditorFile}
        error={fileEditorError}
        prefix="STORE2POG"
        config={{
          prefix: "STORE2POG",
          url: `${Config.API_SERVICE_URL}/api/project-stores`,
          onUploadSuccess: handleFileEditorSuccess,
        }}
        onClose={handleFileEditorClose}
      />

      {/* Passing uploaded file and all user emails to modal */}
      <UsersNotFoundInDatabaseModal
        isOpen={isModalOpen}
        users={notAvailableEmailsInDB}
        duplicateEmails={duplicateEmailsFound}
        chainName={project.chain.name}
        chainId={project.chain.id}
        projectName={project.name}
        projectId={project.id}
        stores={retailStoreNumbers}
        uploadedFile={storeFileRef.current}
        allUserEmails={userEmailAddresses}
        onNo={() => {
          setIsModalOpen(false);
          handleUsersChainCheck(dbAvailableEmails);
        }}
        onClose={() => {
          setIsModalOpen(false);
        }}
        onSubmit={() => {
          setIsModalOpen(false);
          if (dbAvailableEmails.length > 0) {
            proceedWithMapping(dbAvailableEmails);
          }
        }}
      />

      {!isExternalAdmin && <UsersNotFoundModal
        isOpen={isStoreModalOpen}
        users={unMappedUsers}
        onYes={handleYes}
        onNo={handleNo}
        onClose={() => {
          setIsStoreModalOpen(false);
        }}
      />}
      {isErrorModalOpen && (
        <ErrorModal
          open={isErrorModalOpen}
          message={[errorMessages]}
          onClose={() => setIsErrorModalOpen(false)}
        />
      )}

    {/* External Admin Modal to submit user mapping request with unmapped users */}
    {isExternalAdmin && (
            <ExternalAdminModal
              isOpen={isExternalAdminModalOpen}
              users={unMappedUsers}
              chainName={project.chain.name}
              chainId={project.chain.id}
              projectName={project.name}
              projectId={project.id}
              stores={retailStoreNumbers}
              uploadedFile={storeFileRef.current}
              allUserEmails={userEmailAddresses}
              onClose={() => setIsExternalAdminModalOpen(false)}
            />
          )}

      {!isExternalAdmin && <AddUsersNotFoundModal
        isOpen={isAddUserNotFountModalOpen}
        // isOpen={true}
        users={unMappedUsers}
        chainName={project.chain.name}
        onSave={(usersWithRoles) => {
          const chainName = project.chain.name;
          const excelFile = generateUserRolesCSV(chainName, usersWithRoles);

          generatedFileRef.current = excelFile;

          const filesToUpload: File[] = generatedFileRef.current ? [generatedFileRef.current] : [];
          onDrop("CHAINROLEUSER", `${Config.API_SERVICE_URL}/api/chain-user-role`, filesToUpload);
        }}
        onNo={() => {
          setIsAddUserNotFountModalOpen(false);
        }}
        onClose={() => {
          setIsAddUserNotFountModalOpen(false);
        }}
      />}
    </div>
  );
};

const mapStateToProps = (state: State, ownProps: FileUploadOwnProps) => {
  const { prefix } = ownProps;

  return {
    // @ts-ignore
    upload: state[`${prefix}_file_upload`],
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  fetchProject: (id: string | number) => dispatch(fetchProject(id)),
  uploadFile:
    (prefix: string, uploadUrl: string, options?: { offset?: number; timezone?: string }) =>
      (data: FileUploadState) =>
        dispatch(uploadFile(prefix, uploadUrl, options)(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(FileUploadForStep4CP);

const styles = {
  step2Form: {
    padding: "20px",
    fontFamily: "Arial, sans-serif",
  },
  step2Header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
  },
  step2HeaderH2: {
    margin: 0,
    color: "#333",
  },
  fileStatusSelect: {
    padding: "8px 12px",
    border: "1px solid #ddd",
    borderRadius: "4px",
    fontSize: "14px",
  },
  fileStatusContainer: {
    display: "flex",
    alignItems: "center",
  },
  downloadTemplate: {
    color: "#007bff",
    textDecoration: "none",
    fontSize: "14px",
    transition: "color 0.2s ease",
  },
  downloadTemplateHover: {
    color: "#0056b3",
    textDecoration: "underline",
  },
  uploadSection: {
    marginBottom: "30px",
  },
};